import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useListContext } from "ra-core";
import { CircleX } from "lucide-react";

const FILTER_KEY = "tags@ov";

/** Parse "{7,8,9}" → [7, 8, 9] */
function parseTagIds(value: string | undefined): number[] {
  if (!value) return [];
  return value
    .replace(/[{}]/g, "")
    .split(",")
    .filter(Boolean)
    .map(Number);
}

/** Build [7, 8, 9] → "{7,8,9}" */
function buildTagsFilter(ids: number[]): string {
  return `{${ids.join(",")}}`;
}

function toggleTagId(
  tagId: number,
  filterValues: Record<string, any>,
): Record<string, any> {
  const current = parseTagIds(filterValues[FILTER_KEY]);
  const isSelected = current.includes(tagId);
  const next = isSelected
    ? current.filter((id) => id !== tagId)
    : [...current, tagId];

  if (next.length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [FILTER_KEY]: _removed, ...rest } = filterValues;
    return rest;
  }
  return { ...filterValues, [FILTER_KEY]: buildTagsFilter(next) };
}

/**
 * A toggle button for a single tag in the filter sidebar.
 * Multiple tags share the same "tags@cs" filter key — each click adds/removes
 * its ID from the combined set, enabling true multi-select.
 */
export const TagToggleFilterButton = ({
  tagId,
  label,
  className,
  size = "sm",
}: {
  tagId: number;
  label: React.ReactElement | string;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon" | null | undefined;
}) => {
  const { filterValues, setFilters } = useListContext();
  const isSelected = parseTagIds(filterValues[FILTER_KEY]).includes(tagId);

  return (
    <Button
      variant={isSelected ? "secondary" : "ghost"}
      onClick={() => setFilters(toggleTagId(tagId, filterValues))}
      className={cn(
        "cursor-pointer",
        "flex flex-row items-center justify-between gap-2 px-2.5 w-full",
        className,
      )}
      size={size}
    >
      {label}
      {isSelected && <CircleX className="opacity-50" />}
    </Button>
  );
};

/**
 * Shows only when the tag is active; clicking it removes just that tag
 * from the combined filter (used in the active-filter summary bar).
 */
export const ActiveTagFilterButton = ({
  tagId,
  label,
  className,
}: {
  tagId: number;
  label: React.ReactElement | string;
  className?: string;
}) => {
  const { filterValues, setFilters } = useListContext();
  const isSelected = parseTagIds(filterValues[FILTER_KEY]).includes(tagId);
  if (!isSelected) return null;

  return (
    <Button
      variant="secondary"
      onClick={() => setFilters(toggleTagId(tagId, filterValues))}
      className={cn(
        "cursor-pointer",
        "flex flex-row items-center justify-between gap-2 px-2 w-full h-8",
        className,
      )}
      size="sm"
    >
      {label}
      <CircleX className="opacity-50" />
    </Button>
  );
};
